import React from 'react';
import Layout from '../layout/Layout';
import CtaBand from '../layout/CtaBand';
import Section from '../ui/Section';
import Container from '../ui/Container';
import Sparkle from '../ui/Sparkle';
import Reveal from '../ui/Reveal';
import {
  APP, GO_CTA_POST_SLUGS, SHOPSTAR_GO, shopstarGoHref,
} from '../layout/siteData';
import { getPost, relatedPosts } from '../../content/library';
import { isShopstarGoLink, trackGoClick } from '../../lib/track';

const SITE = 'https://www.shopstar.co.za';
const LOGO_URL = `${SITE}/marketing/logos/Shopstar%20Primary%20Logo%20Blue.png`;
const abs = (url) => {
  if (!url) return undefined;
  return url.startsWith('http') ? url : `${SITE}${url}`;
};
const toIsoDate = (value) => {
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? undefined : new Date(ts).toISOString().slice(0, 10);
};

/** Article + BreadcrumbList JSON-LD. Server-rendered (prerender) so crawlers see it. */
const ArticleSchema = ({ post, slug }) => {
  const url = `${SITE}/post/${slug}`;
  const graph = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      ...(post.excerpt ? { description: post.excerpt } : {}),
      ...(abs(post.heroImage) ? { image: abs(post.heroImage) } : {}),
      ...(toIsoDate(post.date) ? { datePublished: toIsoDate(post.date) } : {}),
      ...(post.author ? { author: { '@type': 'Person', name: post.author } } : {}),
      publisher: {
        '@type': 'Organization',
        name: 'Shopstar',
        logo: { '@type': 'ImageObject', url: LOGO_URL },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: url },
      ],
    },
  ];
  const json = JSON.stringify(graph).replace(/</g, '\\u003c');
  // eslint-disable-next-line react/no-danger
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />;
};

// Closing CTA for free-start posts (GO_CTA_POST_SLUGS): point readers at
// Shopstar Go, which is free to start, with the Classic trial as the fallback.
const GO_CTA = {
  eyebrow: 'Want to start for free?',
  title: 'Start selling for free with Shopstar Go.',
  text: `Shopstar Go has no monthly fee and no card needed — you only pay when you sell. ${SHOPSTAR_GO.separateNote}.`,
  primary: { label: 'Explore Shopstar Go', href: shopstarGoHref('blog_post_cta') },
  secondary: { label: 'Or start a 14-day Shopstar trial', href: APP.signUp },
  testId: 'blog-post-go-cta',
};

// Post body HTML — static catalog or Outrank-fed DB — rendered as rich prose.
// Go links inside it are plain anchors (some added server-side by
// Marketing::GoLinker), so one delegated listener tracks their clicks.
const PostBody = ({ html }) => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onClick = (e) => {
      const link = e.target.closest && e.target.closest('a');
      if (link && isShopstarGoLink(link.href)) trackGoClick(link.href, link.textContent);
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, []);
  // eslint-disable-next-line react/no-danger
  return <div ref={ref} className="ss-prose" data-testid="blog-post-body" dangerouslySetInnerHTML={{ __html: html }} />;
};

const NotFound = () => (
  <Layout>
    <Section tone="cream" pad="lg">
      <Container>
        <h1 className="text-h1 font-extrabold text-ink">Post not found</h1>
        <p className="mt-3"><a href="/blog" className="text-ink font-bold">← Back to the blog</a></p>
      </Container>
    </Section>
  </Layout>
);

// `post`/`related` (which may be static or DB-backed) are passed by the
// controller. Falls back to the build-time static lookup when absent.
const BlogPost = ({ slug, post: postProp, related: relatedProp }) => {
  const post = postProp || getPost(slug);
  if (!post) return <NotFound />;
  const meta = [post.date, post.readMinutes && `${post.readMinutes} min read`, post.author].filter(Boolean).join(' · ');
  const related = Array.isArray(relatedProp) ? relatedProp : relatedPosts(slug, 3);

  return (
    <Layout>
      <ArticleSchema post={post} slug={slug} />
      <article>
        <header className="relative overflow-hidden bg-sky">
          <Sparkle size={110} color="#95a8e6" className="pointer-events-none absolute -top-3 right-[6%] opacity-25 hidden sm:block" />
          <Container className="relative max-w-[820px] py-12 md:py-16">
            <a href="/blog" className="group inline-flex items-center gap-1.5 font-bold text-ink mb-5"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="transition-transform group-hover:-translate-x-0.5"><path d="M19 12H5M11 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>The blog</a>
            <h1 className="text-[clamp(2rem,4.5vw,3.2rem)] font-extrabold text-ink leading-[1.05] tracking-tight max-w-[20ch]">{post.title}</h1>
            <span className="block mt-5 text-xs font-bold uppercase tracking-wide text-ink">{meta}</span>
          </Container>
        </header>

        {post.heroImage && (
          <Container>
            <img src={post.heroImage} alt={post.title} loading="eager" className="w-full max-w-[960px] mx-auto block aspect-[16/8] object-cover rounded-4xl shadow-card mt-8 md:mt-10" />
          </Container>
        )}

        <Section tone="white" pad="md">
          <div className="max-w-prose mx-auto">
            <PostBody html={post.bodyHtml} />
          </div>
        </Section>
      </article>

      {related.length > 0 && (
        <Section tone="cream" pad="lg">
          <h2 className="text-h2 font-extrabold text-ink mb-9">Keep reading</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 70}>
                <a href={`/post/${p.slug}`} className="group block bg-white border border-line rounded-3xl overflow-hidden hover:-translate-y-1 hover:shadow-card transition-all">
                  <div className="aspect-[16/10] bg-cream overflow-hidden">{p.heroImage && <img src={p.heroImage} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}</div>
                  <h3 className="text-lg font-extrabold text-ink leading-snug px-6 pt-5">{p.title}</h3>
                  <span className="block px-6 pb-6 pt-2.5 text-[0.82rem] text-muted font-semibold">{[p.date, p.author].filter(Boolean).join(' · ')}</span>
                </a>
              </Reveal>
            ))}
          </div>
        </Section>
      )}

      {GO_CTA_POST_SLUGS.includes(slug)
        ? <CtaBand {...GO_CTA} />
        : <CtaBand title="Ready to start selling?" text="Put what you’ve learned into action with a free 14-day trial." />}
    </Layout>
  );
};

export default BlogPost;
